/**
 * Comparacao entre a janela atual e a anterior, projecao e economia
 * unitaria -- tudo em inteiros.
 *
 * Decisoes nao obvias:
 *  1. A variacao relativa sai em BPS inteiros (1234 = +12,34%), calculada
 *     uma vez aqui e formatada por `formatPercentFromBps`. Nenhum componente
 *     faz `(a - b) / b` por conta propria: e assim que dois cartoes da mesma
 *     tela nunca discordam sobre a mesma variacao.
 *  2. `sem_base` e um estado, nao um zero. Anterior nulo ("desde o inicio")
 *     ou anterior zero com atual positivo nao tem percentual honesto; a tela
 *     mostra a variacao absoluta e diz que nao havia base.
 *  3. Direcao e fato aritmetico; se e boa ou ruim, quem decide e o
 *     consumidor (custo subir junto com o volume nao e "ruim").
 *  4. Economia unitaria (custo por venda, comissao por venda) divide centavos
 *     por contagem e arredonda para centavo inteiro: e exibicao, nunca base
 *     de fatura. Denominador zero devolve `null`, e a tela escreve isso.
 */

export type Direcao = 'alta' | 'queda' | 'igual' | 'sem_base';

export interface Variacao {
  readonly direcao: Direcao;
  /** `atual - anterior`, na unidade da medida. Zero quando sem base. */
  readonly absoluta: number;
  /** Variacao relativa em bps; `null` quando nao ha base para dividir. */
  readonly relativaBps: number | null;
}

export const SEM_BASE: Variacao = { direcao: 'sem_base', absoluta: 0, relativaBps: null };

export function variar(atual: number, anterior: number | null): Variacao {
  if (anterior === null || !Number.isFinite(anterior) || !Number.isFinite(atual)) {
    return SEM_BASE;
  }
  const absoluta = atual - anterior;
  if (absoluta === 0) {
    return { direcao: 'igual', absoluta: 0, relativaBps: 0 };
  }
  if (anterior === 0) {
    return { direcao: absoluta > 0 ? 'alta' : 'queda', absoluta, relativaBps: null };
  }
  return {
    direcao: absoluta > 0 ? 'alta' : 'queda',
    absoluta,
    relativaBps: Math.round((absoluta * 10_000) / Math.abs(anterior)),
  };
}

/** Queda de pelo menos `limiarBps` pontos-base numa medida que ja esta em bps (taxa). */
export function caiuEmPontos(atualBps: number, anteriorBps: number | null, limiarBps: number): boolean {
  return anteriorBps !== null && anteriorBps - atualBps >= limiarBps;
}

/**
 * Projecao linear inteira: o que `realizado` em `decorridos` dias vira em
 * `total` dias no mesmo ritmo. Sem dia decorrido, nao ha ritmo: devolve null.
 */
export function projetar(realizado: number, decorridos: number, total: number): number | null {
  if (decorridos <= 0 || total <= 0) {
    return null;
  }
  return Math.round((realizado * total) / decorridos);
}

/** Centavos por unidade, inteiro; `null` sem unidade para dividir. */
export function porUnidade(cents: number, unidades: number): number | null {
  if (unidades <= 0 || !Number.isFinite(cents)) {
    return null;
  }
  return Math.round(cents / unidades);
}

/** Participacao de `parte` em `todo`, em bps; `null` quando `todo` e zero. */
export function participacaoBps(parte: number, todo: number): number | null {
  if (todo <= 0) {
    return null;
  }
  return Math.round((parte * 10_000) / todo);
}

/** Dias inteiros entre dois ISO (fim exclusivo), no minimo 1. */
export function diasEntre(inicioIso: string, fimIso: string): number {
  const inicio = new Date(inicioIso).getTime();
  const fim = new Date(fimIso).getTime();
  if (Number.isNaN(inicio) || Number.isNaN(fim) || fim <= inicio) {
    return 1;
  }
  return Math.max(1, Math.round((fim - inicio) / 86_400_000));
}

/** Dias inteiros de `desdeIso` ate `ateIso`, para "em implantacao ha N dias". */
export function diasDesde(desdeIso: string, ateIso: string): number {
  const desde = new Date(desdeIso).getTime();
  const ate = new Date(ateIso).getTime();
  if (Number.isNaN(desde) || Number.isNaN(ate) || ate <= desde) {
    return 0;
  }
  return Math.floor((ate - desde) / 86_400_000);
}
